import React from 'react'
import { Route, Redirect, Switch } from 'react-router-dom'
import { ApolloProvider } from '@apollo/client'
import NavigationBar from 'transfix-ui/components/NavigationBar'
import { QueryParamProvider } from 'use-query-params'
import { getApiUrl } from 'utils/api/apiHelpers'
import { BreakpointProvider } from 'utils/context/BreakpointContext'
import { useFeatureFlag } from 'utils/context/FeatureFlagsContext'
import { marketplaceClient, marketplaceBatchClient } from 'utils/graphql/client'
import { useUserDetails } from 'utils/hooks/useUserDetails'
import { RouteNotFound } from 'components/404'
import { AuthenticatedRoute } from 'components/AuthenticatedRoute'
import { ErrorBoundary } from 'components/ErrorBoundary'
import { RogersRestfulProvider } from 'components/RogersRestfulProvider'
import { Footer } from 'components/layout'
import { GlobalToast } from 'transfix-ui'
import { ConnectedPhoneNumberProvider } from '../components/ConnectedPhoneNumber'
import AccountingTools from './accounting-tools/indexAsync'
import AmQuotingTool from './am-quoting-tool/indexAsync'
import AwardedLanes from './awarded-lanes'
import CarrierOptionsPage from './carrier-options/indexAsync'
import CostAudit from './cost-audit/indexAsync'
import CreateShipment from './create-shipment'
import Facilities from './facilities/indexAsync'
import Groups from './groups/indexAsync'
import InstantRfp from './instant-rfp'
import Automation from './issues/automation'
import ControlPanel from './issues/control-panel'
import Issues from './issues/indexAsync'
import LaneAssignment from './lane/assignment/indexAsync'
import Lanes from './lane/transfixLaneId/indexAsync'
import PresourceCarriers from './presource-carriers/indexAsync'
import RFPLaneRates from './rfp-lanes/indexAsync'
import ShipperLanes from './rms/shipper-lanes/indexAsync'
import ShipmentCoverage from './shipment-details/ShipmentCoverage'
import ShipmentDetails from './shipment-details/indexAsync'
import ShipperRateMatcher from './shipper-rate-matcher/indexAsync'
import ShipperProfile from './shippers/indexAsync'
import HistoricalQuotes from './spot-quote-tool/historical-quotes'
import SpotQuoteTool from './spot-quote-tool/indexAsync'
/* MARKETPLACE PAGES */
import StaplesHeadhaulLanes from './staples-headhaul-lanes'
import TaskAssignments from './task-assignments/indexAsync'

export function App() {
  const { logout } = useUserDetails()
  const isGraphqlBatchingEnabled = useFeatureFlag('graphqlBatching')

  const apolloClient = isGraphqlBatchingEnabled ? marketplaceBatchClient : marketplaceClient

  return (
    <ApolloProvider client={apolloClient}>
      <RogersRestfulProvider
        baseApi={`${getApiUrl('MARKETPLACE')}`}
        onUnauthenticatedResponse={logout}>
        <ConnectedPhoneNumberProvider>
          <BreakpointProvider>
            <QueryParamProvider ReactRouterRoute={Route}>
              <ErrorBoundary>
                <GlobalToast />
                <NavigationBar />
                <section className='container-fluid pt8'>
                  <Switch>
                    <Redirect from='/payments' to='/fms/login/' />
                    <AuthenticatedRoute
                      exact
                      path='/accounting-tools'
                      component={AccountingTools}
                    />
                    <AuthenticatedRoute
                      exact
                      path='/shipment-details/:shipmentId'
                      component={ShipmentDetails}
                    />
                    <AuthenticatedRoute
                      exact
                      path='/shipment-details/:shipmentId/coverage'
                      component={ShipmentCoverage}
                    />
                    <AuthenticatedRoute
                      exact
                      path='/staples-headhaul-lanes'
                      component={StaplesHeadhaulLanes}
                    />
                    <AuthenticatedRoute exact path='/awarded-lanes' component={AwardedLanes} />
                    <AuthenticatedRoute exact path='/awarded-lanes/:id' component={AwardedLanes} />
                    <AuthenticatedRoute exact path='/instant-rfp' component={InstantRfp} />
                    <AuthenticatedRoute exact path='/rfp/:rfpId' component={CostAudit} />
                    <AuthenticatedRoute
                      exact
                      path='/shipper-rate-matcher'
                      component={ShipperRateMatcher}
                    />

                    <AuthenticatedRoute path='/task-assignments' component={TaskAssignments} />

                    <Redirect from='/cx-assignments' to='/task-assignments' />
                    <AuthenticatedRoute exact path='/lane' component={Lanes} />
                    <AuthenticatedRoute exact path='/lane/assignment' component={LaneAssignment} />
                    <AuthenticatedRoute exact path='/lane/:transfixLaneId' component={Lanes} />
                    <AuthenticatedRoute
                      exact
                      path='/lane/:transfixLaneId/:specificLaneId'
                      component={Lanes}
                    />
                    <AuthenticatedRoute
                      exact
                      path='/presource-carriers'
                      component={PresourceCarriers}
                    />
                    <Redirect from='/rate-confirmation/:id' to='/fms/book/:id/rate-confirmation' />
                    <AuthenticatedRoute exact path='/rfp-lanes' component={RFPLaneRates} />
                    <AuthenticatedRoute exact path='/rms/shipper-lanes' component={ShipperLanes} />
                    <AuthenticatedRoute
                      exact
                      path='/rms/shipper-lanes/:transfixShipperLaneId'
                      component={ShipperLanes}
                    />
                    <AuthenticatedRoute path='/facilities' component={Facilities} />
                    <AuthenticatedRoute exact path='/am-quoting-tool' component={AmQuotingTool} />
                    <AuthenticatedRoute exact path='/spot-quote-tool' component={SpotQuoteTool} />
                    <AuthenticatedRoute
                      exact
                      path='/spot-quote-tool/search'
                      component={HistoricalQuotes}
                    />
                    <AuthenticatedRoute
                      path='/carrier-options/:id?'
                      component={CarrierOptionsPage}
                    />
                    <AuthenticatedRoute path='/shippers' component={ShipperProfile} />
                    <AuthenticatedRoute exact path='/issues' component={Issues} />
                    <AuthenticatedRoute
                      exact
                      path='/issues/control-panel'
                      component={ControlPanel}
                    />
                    <AuthenticatedRoute
                      exact
                      path='/issues/:id/automation'
                      component={Automation}
                    />
                    <AuthenticatedRoute
                      exact
                      path='/issues/:shipmentId/'
                      render={({ match = {} }) => {
                        const { params: { shipmentId = '' } = {} } = match

                        return <Redirect to={`/shipment-details/${shipmentId}?newIssueModal=1`} />
                      }}
                    />
                    <AuthenticatedRoute
                      exact
                      path='/issues/:shipmentId/:id'
                      render={({ match = {} }) => {
                        const { params: { id = '', shipmentId = '' } = {} } = match

                        return <Redirect to={`/shipment-details/${shipmentId}?issueId=${id}`} />
                      }}
                    />
                    <AuthenticatedRoute path='/groups' component={Groups} />
                    <AuthenticatedRoute path='/create-shipment' component={CreateShipment} />
                    <Route component={RouteNotFound} />
                  </Switch>
                </section>
                <Footer />
              </ErrorBoundary>
            </QueryParamProvider>
          </BreakpointProvider>
        </ConnectedPhoneNumberProvider>
      </RogersRestfulProvider>
    </ApolloProvider>
  )
}
